/**
 * The bar along the bottom of the page area. §6.2.
 *
 * Canva's arrangement: which page, and a list of them, on the left; on the
 * right the view - rulers, grid, preview - then zoom as a slider with its
 * number, fit, full screen and help. Docked rather than floating, so it never
 * sits on the artwork.
 */

import { useEffect, useRef, useState } from 'react';
import { useEditor } from '../state/store';
import { MAX_ZOOM, MIN_ZOOM } from '../canvas/CanvasView';
import { FIT_TOP } from '../canvas/stack';
import { mmToPx } from '../model/units';
import { IconFit, IconFullscreen, IconGrid, IconGuides, IconHelp, IconPages, IconPreview, IconRuler } from './icons';
import { gridLabel } from '../canvas/Rulers';

/** The slider's own range. Beyond it the buttons and the wheel still reach. */
const SLIDE_MIN = 0.1;
const SLIDE_MAX = 8;

export function CanvasFoot( { onFit, onHelp }: { onFit: () => void; onHelp?: () => void } ) {
  const doc = useEditor( ( s ) => s.doc );
  const pageIndex = useEditor( ( s ) => s.pageIndex );
  const view = useEditor( ( s ) => s.view );
  const setView = useEditor( ( s ) => s.setView );
  const unit = useEditor( ( s ) => s.unit );
  const bar = useRef<HTMLDivElement>( null );

  if ( ! doc ) return null;

  /** Zooms about the middle of the page area, which is what is being looked at. */
  const zoomTo = ( next: number ) => {
    const zoom = Math.max( MIN_ZOOM, Math.min( MAX_ZOOM, next ) );
    const area = bar.current?.parentElement?.getBoundingClientRect();
    const cx = area ? area.width / 2 : 0;
    const cy = area ? area.height / 2 : 0;
    const ratio = zoom / view.zoom;

    setView( { zoom, x: cx - ( cx - view.x ) * ratio, y: cy - ( cy - view.y ) * ratio } );
  };

  // The slider moves along a logarithmic scale: 10% to 100% is as much of its
  // travel as 100% to 800%, which is how zoom feels.
  const toSlider = ( z: number ) => Math.round( ( Math.log( z / SLIDE_MIN ) / Math.log( SLIDE_MAX / SLIDE_MIN ) ) * 1000 );
  const fromSlider = ( v: number ) => SLIDE_MIN * Math.pow( SLIDE_MAX / SLIDE_MIN, v / 1000 );

  return (
    <div className="tb-foot" ref={ bar } role="toolbar" aria-label="View">
      <PagesMenu />

      <div className="tb-foot__spacer" />

      <Toggle on={ view.showRulers } label="Rulers (R)" onClick={ () => setView( { showRulers: ! view.showRulers } ) }>
        <IconRuler />
      </Toggle>
      <Toggle on={ view.showGrid } label={ `Grid, ${ gridLabel( unit ) } (G)` } onClick={ () => setView( { showGrid: ! view.showGrid } ) }>
        <IconGrid />
      </Toggle>
      {/* Drag a guide off a ruler to make one; this is where they are hidden again. */}
      <Toggle on={ view.showGuides } label="Guides (;)" onClick={ () => setView( { showGuides: ! view.showGuides } ) }>
        <IconGuides />
      </Toggle>
      <Toggle on={ view.preview } label="Preview without guides (W)" onClick={ () => setView( { preview: ! view.preview } ) }>
        <IconPreview />
      </Toggle>

      <span className="tb-foot__sep" role="separator" />

      <input
        className="tb-foot__zoom"
        type="range"
        min={ 0 }
        max={ 1000 }
        value={ toSlider( Math.max( SLIDE_MIN, Math.min( SLIDE_MAX, view.zoom ) ) ) }
        aria-label="Zoom"
        onChange={ ( event ) => zoomTo( fromSlider( parseFloat( event.target.value ) ) ) }
      />

      <ZoomMenu zoom={ view.zoom } onZoom={ zoomTo } onFit={ onFit } />

      <button type="button" className="tb-foot__btn" onClick={ onFit } aria-label="Fit the page on screen" title="Fit (Ctrl+0)">
        <IconFit />
      </button>

      <button
        type="button"
        className="tb-foot__btn"
        aria-label="Full screen"
        title="Full screen"
        onClick={ () => {
          const app = bar.current?.closest( '.tb-app' );

          if ( document.fullscreenElement ) void document.exitFullscreen();
          else if ( app && 'requestFullscreen' in app ) void ( app as HTMLElement ).requestFullscreen();
        } }
      >
        <IconFullscreen />
      </button>

      { onHelp && (
        <button type="button" className="tb-foot__btn" aria-label="Help and shortcuts" title="Keyboard shortcuts (?)" onClick={ onHelp }>
          <IconHelp />
        </button>
      ) }

      {/* Which page is being edited, said for anyone not looking at the page titles. */}
      <span className="tb-sr" aria-live="polite">
        Page { pageIndex + 1 } of { doc.pages.length }
      </span>
    </div>
  );
}

function Toggle( { on, label, onClick, children }: { on: boolean; label: string; onClick: () => void; children: React.ReactNode } ) {
  return (
    <button type="button" className={ `tb-foot__btn${ on ? ' is-on' : '' }` } aria-pressed={ on } aria-label={ label } title={ label } onClick={ onClick }>
      { children }
    </button>
  );
}

/** "Pages 1 / 2", opening a list of every page to jump to. */
function PagesMenu() {
  const doc = useEditor( ( s ) => s.doc );
  const pageIndex = useEditor( ( s ) => s.pageIndex );
  const [ open, setOpen ] = useState( false );
  const wrap = useRef<HTMLDivElement>( null );

  useDismiss( open, wrap, () => setOpen( false ) );

  if ( ! doc ) return null;

  /** Makes a page the active one and scrolls it to the top of the view. */
  const show = ( index: number ) => {
    const store = useEditor.getState();

    store.goToPage( index );
    store.setView( { y: FIT_TOP + mmToPx( doc.bleed, store.view.zoom ) } );
    setOpen( false );
  };

  return (
    <div className="tb-foot__pages" ref={ wrap }>
      <button type="button" className="tb-foot__pagebtn" aria-haspopup="menu" aria-expanded={ open } onClick={ () => setOpen( ! open ) }>
        <IconPages />
        <span>Pages</span>
        <span className="tb-foot__count tb-num">
          { pageIndex + 1 } / { doc.pages.length }
        </span>
      </button>

      { open && (
        <div className="tb-foot__menu" role="menu" aria-label="Pages">
          { doc.pages.map( ( page, index ) => (
            <button
              key={ page.id }
              type="button"
              role="menuitem"
              className={ `tb-pagebtn${ index === pageIndex ? ' is-on' : '' }` }
              aria-current={ index === pageIndex }
              onClick={ () => show( index ) }
            >
              <span className="tb-pagebtn__n tb-num">{ index + 1 }</span>
              { page.name }
            </button>
          ) ) }
        </div>
      ) }
    </div>
  );
}

/** The zoom number, which opens a few set sizes. */
function ZoomMenu( { zoom, onZoom, onFit }: { zoom: number; onZoom: ( z: number ) => void; onFit: () => void } ) {
  const [ open, setOpen ] = useState( false );
  const wrap = useRef<HTMLDivElement>( null );

  useDismiss( open, wrap, () => setOpen( false ) );

  return (
    <div className="tb-foot__pages" ref={ wrap }>
      <button type="button" className="tb-foot__zoomnum tb-num" aria-haspopup="menu" aria-expanded={ open } onClick={ () => setOpen( ! open ) }>
        { Math.round( zoom * 100 ) }%
      </button>

      { open && (
        <div className="tb-foot__menu tb-foot__menu--right" role="menu" aria-label="Zoom">
          <button type="button" role="menuitem" className="tb-pagebtn" onClick={ () => { onFit(); setOpen( false ); } }>
            Fit
          </button>
          { [ 0.5, 1, 2, 3 ].map( ( z ) => (
            <button key={ z } type="button" role="menuitem" className="tb-pagebtn" onClick={ () => { onZoom( z ); setOpen( false ); } }>
              { z * 100 }%
            </button>
          ) ) }
        </div>
      ) }
    </div>
  );
}

/** Clicking away or Escape closes a menu - and Escape stops there. */
function useDismiss( open: boolean, wrap: React.RefObject<HTMLDivElement | null>, close: () => void ) {
  useEffect( () => {
    if ( ! open ) return;

    const away = ( event: MouseEvent ) => {
      if ( wrap.current && ! wrap.current.contains( event.target as Node ) ) close();
    };
    const key = ( event: KeyboardEvent ) => {
      if ( event.key !== 'Escape' ) return;

      event.preventDefault();
      close();
    };

    window.addEventListener( 'mousedown', away );
    window.addEventListener( 'keydown', key, true );

    return () => {
      window.removeEventListener( 'mousedown', away );
      window.removeEventListener( 'keydown', key, true );
    };
  }, [ open, wrap, close ] );
}
